import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

// Stripe
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

// Axios for sending email
import axios from "axios";

// Bootstrap components
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";

// Custom CSS
// import "../../assets/scss/pages/payment.scss";
import "./paymentForm.styles.scss";

// Custom components
import OrderSummary from "../OrderSummary/OrderSummary";
import CardSection from "../CardSection/CardSection";

// Formik for form validation
import { Formik } from "formik";
import * as yup from "yup";

// API
import { getMeApi } from "../../api/user";
import { createOrder } from "../../api/order";
import { getProductsCart } from "../../api/cart";
import { createPaymentIntent, confirmPayment } from "../../api/stripe";

// Hooks
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";

// Utils
import { v4 as uuidv4 } from "uuid";
import formatMoney from "../../utils/formatMoney";

// Assets
import lockIconUrl from "../../assets/images/lock-icon.svg";
import creditCard1Url from "../../assets/images/credit-cards_visa.svg";
import creditCard2Url from "../../assets/images/credit-cards_mastercard.svg";
import creditCard3Url from "../../assets/images/credit-cards_amex.svg";

// Initial values will be created dynamically in the component

let schema = yup.object().shape({
  clientName: yup.string().required("Este campo es requerido"),
  // lastName: yup.string().required("Este campo es requerido"),
  clientEmail: yup
    .string()
    .email("El email no es válido")
    .required("Este campo es requerido"),
  // .isValid("El email no es válido"),
  clientPhoneNumber: yup.string().required("Este campo es requerido"),
  shipping: yup.object().shape({
    method: yup.string().required("Este campo es requerido")
  }),
  address: yup.string().required("Este campo es requerido"),
  streetNumber: yup.string().required("Este campo es requerido"),
  neighborhood: yup.string().required("Este campo es requerido"),
  reference: yup.string().required("Este campo es requerido"),
  postalCode: yup
    .number()
    .required("Este campo es requerido")
    .moreThan(10000, "Ingresa un Código Postal válido"),
  municipality: yup.string().required("Este campo es requerido"),
  city: yup.string().required("Este campo es requerido"),
  state: yup.string().required("Este campo es requerido"),
});

export default function PaymentForm() {
  const [user, setUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingOrderCreation, setLoadingOrderCreation] = useState(false);
  // const [cartItems, setCartItems] = useState([]);
  const { auth, logout, loading: authLoading } = useAuth();

  // Create initial values with user data from auth context
  const getInitialValues = () => {
    const userData = auth?.user || {};

    return {
      clientName: userData.fullName || "",
      clientEmail: userData.email || "",
      clientPhoneNumber: userData.phoneNumber || "",
      shipping: {
        method: "premium"
      },
      address: "",
      streetNumber: "",
      neighborhood: "",
      postalCode: 0,
      reference: "",
      municipality: "",
      city: "",
      state: "",
    };
  };
  const {
    productsCart,
    resetCart,
    totalPriceCart,
    cartItems,
    totalProductsCart,
  } = useCart();
  const [shipment, setShipment] = useState(0);
  // const [error, setError] = useState("");
  const [formError, setFormError] = useState();
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState("single");
  const [installmentOptions, setInstallmentOptions] = useState([]);
  const [installmentsEnabled, setInstallmentsEnabled] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [cardDetailsCollected, setCardDetailsCollected] = useState(false);
  const [criticalError, setCriticalError] = useState(false);
  const history = useHistory();
  // console.log(total);

  const stripe = useStripe();
  const elements = useElements();


  // Check if installments should be available (minimum amount requirement)
  useEffect(() => {
    // Stripe requires minimum 300 MXN for installments
    if (totalPriceCart >= 300) {
      setInstallmentsEnabled(true);
    } else {
      setInstallmentsEnabled(false);
      setInstallmentOptions([]);
      setSelectedPaymentPlan("single");
    }
  }, [totalPriceCart]);

  // Step 1: Collect payment method details and create PaymentIntent
  const handleCardDetailsSubmit = async () => {
    if (!stripe || !elements) {
      setFormError("Stripe no está disponible. Por favor, recarga la página.");
      return;
    }

    setLoading(true);
    setFormError("");

    try {
      // Step 1: Create PaymentMethod from card details
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });

      if (error) {
        setFormError(`Error con la tarjeta: ${error.message}`);
        setLoading(false);
        return;
      }

      // Step 2: Create PaymentIntent with the PaymentMethod attached
      const paymentIntentResponse = await createPaymentIntent(totalPriceCart, paymentMethod.id);

      if (paymentIntentResponse.error) {
        setFormError(paymentIntentResponse.error);
        setLoading(false);
        return;
      }

      setPaymentIntentId(paymentIntentResponse.paymentIntentId);

      // Step 3: Check if installments are available for this card
      if (paymentIntentResponse.availablePlans && paymentIntentResponse.availablePlans.length > 0) {
        console.log('Available plans:', paymentIntentResponse.availablePlans);
        setAvailablePlans(paymentIntentResponse.availablePlans);
        setCardDetailsCollected(true);
      } else {
        // No installments available, proceed with single payment
        console.log('No installments available for this card');
        console.log('PaymentIntent response:', paymentIntentResponse);
        setSelectedPaymentPlan("single");
        setCardDetailsCollected(true);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error collecting card details:', error);
      setFormError("Error al procesar los datos de la tarjeta. Por favor, intenta de nuevo.");
      setLoading(false);
    }
  };

  // Step 2: Handle installment plan selection
  const handlePlanSelection = (plan) => {
    if (plan === null || plan === "single") {
      setSelectedPaymentPlan("single");
    } else {
      setSelectedPaymentPlan(plan.count.toString());
    }
  };

  // Step 3: Confirm payment with selected plan
  const handlePaymentConfirmation = async (values) => {
    if (!paymentIntentId) {
      setFormError("Error: No se pudo crear la intención de pago.");
      return;
    }

    setLoading(true);
    setFormError("");

    try {
      let confirmData = {};

      // If installments are selected, include the plan
      let selectedPlan = null;
      if (selectedPaymentPlan !== "single") {
        selectedPlan = availablePlans.find(plan => plan.count.toString() === selectedPaymentPlan);
      }

      // Confirm the PaymentIntent
      const confirmResponse = await confirmPayment(paymentIntentId, selectedPlan);

      if (confirmResponse.error) {
        setFormError(confirmResponse.error);
        setLoading(false);
        return;
      }

      // Payment successful, proceed with order creation
      await createOrderWithPayment(values, confirmResponse.paymentIntentId);

    } catch (error) {
      console.error('Error confirming payment:', error);

      // Handle specific installment errors
      if (error.message.includes('installment') || error.message.includes('plan')) {
        setFormError("El plan de meses sin intereses seleccionado no está disponible para esta tarjeta. Por favor selecciona otra opción.");
      } else {
        setFormError("Error al procesar el pago. Por favor, verifica los datos de tu tarjeta.");
      }
      setLoading(false);
    }
  };

  // Create order after successful payment
  const createOrderWithPayment = async (values, finalPaymentIntentId) => {
    setLoadingOrderCreation(true);

    try {
      const today = new Date();
      const date = today.getFullYear() + "-" + (today.getMonth() + 1) + "-" + today.getDate();
      const orderId = uuidv4().slice(0, 7);

      const orderItemsFormatted = cartItems.map((item) => ({
        product: item.product.id,
        quantity: item.quantity,
      }));

      // Get the selected plan object for backend
      let selectedPlanForBackend = null;
      if (selectedPaymentPlan !== "single") {
        selectedPlanForBackend = availablePlans.find(plan => plan.count.toString() === selectedPaymentPlan);
      }

      const formDataTemp = {
        token: finalPaymentIntentId,
        orderId,
        clientName: values.clientName,
        clientEmail: values.clientEmail,
        clientPhoneNumber: values.clientPhoneNumber,
        shipping: values.shipping,
        address: values.address,
        streetNumber: values.streetNumber,
        neighborhood: values.neighborhood,
        postalCode: parseInt(values.postalCode),
        reference: values.reference,
        municipality: values.municipality,
        city: values.city,
        state: values.state,
        country: "México",
        total: totalPriceCart,
        orderItems: cartItems.map((item) => ({
          product: {
            id: item.product.id,
            nombre: item.product.nombre,
            precio: item.product.precio,
            eta: item.product.eta,
          },
          quantity: item.quantity,
        })),
        installments_enabled: selectedPaymentPlan !== 'single',
        installments_meses: selectedPaymentPlan !== 'single' ? parseInt(selectedPaymentPlan) : null,
        installments_pago_mensual: selectedPaymentPlan !== 'single' ? totalPriceCart / parseInt(selectedPaymentPlan) : null,
        installments_stripe_payment_intent_id: finalPaymentIntentId,
        selected_plan: selectedPlanForBackend,
        usuario: auth.idUser,
      };

      const response = await createOrder(formDataTemp, auth);

      // Debug logging to understand response structure
      console.log('CreateOrder response:', response);
      console.log('Response error:', response?.error);
      console.log('Response errorCode:', response?.errorCode);

      // Check if response indicates an error (after successful payment)
      if (response?.error || response?.errorCode || !response?.doc) {
        console.error('Order creation failed after successful payment:', {
          paymentIntentId: finalPaymentIntentId,
          error: response.error,
          errorCode: response.errorCode,
          message: response.message,
          fullResponse: response,
          userId: auth.idUser,
          orderData: formDataTemp
        });

        // For stock issues (400 error), payment wasn't processed
        if (response.errorCode === 400) {
          setFormError("Uno o más productos que deseas comprar ya se encuentran fuera de stock. No se te hizo ningún cargo.");
        } else {
          // For any other error after successful payment, show critical error
          setCriticalError(true);
          setFormError(`⚠️ IMPORTANTE: Tu pago fue procesado exitosamente, pero hubo un error al crear tu orden. Por favor contacta a soporte inmediatamente con este ID de pago: ${finalPaymentIntentId}. WhatsApp: +52 55 6875 7619. Te ayudaremos a resolver este problema y procesar tu orden correctamente.`);
        }
      } else {
        // Success case - clear cart and redirect to confirmation
        await resetCart(null, auth);
        history.push(`/confirmation/${response.doc.orderId}`);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      console.error('Critical: Payment succeeded but order creation failed:', {
        paymentIntentId: finalPaymentIntentId,
        error: error.message,
        userId: auth.idUser,
        timestamp: new Date().toISOString()
      });
      setCriticalError(true);
      setFormError(`⚠️ IMPORTANTE: Tu pago fue procesado exitosamente, pero hubo un error inesperado al crear tu orden. Por favor contacta a soporte inmediatamente con este ID de pago: ${finalPaymentIntentId}. WhatsApp: +52 55 6875 7619. No intentes realizar el pago nuevamente.`);
    } finally {
      setLoading(false);
      setLoadingOrderCreation(false);
    }
  };

  // Reset form state
  const resetPaymentForm = () => {
    setCardDetailsCollected(false);
    setPaymentIntentId(null);
    setAvailablePlans([]);
    setSelectedPaymentPlan("single");
    setFormError("");
    if (elements) {
      elements.getElement(CardElement).clear();
    }
  };

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <Row>
        <Col xs="12" className="text-center py-5">
          <div>Cargando datos del usuario...</div>
        </Col>
      </Row>
    );
  }

  return (
    <Row>
      <Col xs="12" lg="8" className="client-data">
        <div className="client-data__address mt-4">
          <h5>
            <span>1</span> Datos de envío
          </h5>
          <Formik
            key={auth?.user?.id || 'no-user'} // Force re-render when user changes
            initialValues={getInitialValues()}
            enableReinitialize={true}
            validationSchema={schema}
            onSubmit={async (values, { setSubmitting }) => {
              if (!cardDetailsCollected) {
                // Step 1: Collect card details first
                await handleCardDetailsSubmit();
              } else {
                // Step 3: Confirm payment with selected plan
                await handlePaymentConfirmation(values);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              /* and other goodies */
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Form.Group
                    as={Col}
                    lg="6"
                    className="mb-3 form-group"
                  // controlId="formGridName"
                  >
                    <Form.Label>Nombre Completo:</Form.Label>
                    <Form.Control
                      name="clientName"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.clientName}
                      isInvalid={errors.clientName}
                      disabled={isSubmitting}
                      placeholder="Tu nombre"
                    />
                    <div className="error-feedback">
                      {errors.clientName &&
                        touched.clientName &&
                        errors.clientName}
                    </div>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    lg="6"
                    className="mb-3 form-group"
                  // controlId="formGridEmail"
                  >
                    <Form.Label>Correo electrónico:</Form.Label>
                    <Form.Control
                      name="clientEmail"
                      type="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.clientEmail}
                      isInvalid={errors.clientEmail}
                      disabled={isSubmitting}
                      placeholder="Email"
                    />
                    <div className="error-feedback">
                      {errors.clientEmail &&
                        touched.clientEmail &&
                        errors.clientEmail}
                    </div>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    lg="6"
                    className="mb-3 form-group"
                  // controlId="formGridPhone"
                  >
                    <Form.Label>Teléfono:</Form.Label>
                    <Form.Control
                      name="clientPhoneNumber"
                      type="tel"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.clientPhoneNumber}
                      isInvalid={errors.clientPhoneNumber}
                      disabled={isSubmitting}
                      placeholder="Teléfono"
                    />
                    <div className="error-feedback">
                      {errors.clientPhoneNumber &&
                        touched.clientPhoneNumber &&
                        errors.clientPhoneNumber}
                    </div>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    lg="6"
                    className="mb-3 form-group"
                  >
                    <Form.Label>Método de envío:</Form.Label>
                    <Form.Select
                      name="shipping.method"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.shipping.method}
                      isInvalid={errors.shipping?.method}
                      disabled={isSubmitting}
                    >
                      <option value="premium">Envío Gratuito Premium</option>
                      <option value="express">Envío Express</option>
                      <option value="overnight">Entrega al Día Siguiente</option>
                    </Form.Select>
                    <div className="error-feedback">
                      {errors.shipping?.method &&
                        touched.shipping?.method &&
                        errors.shipping.method}
                    </div>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    lg="12"
                    className="mb-3 form-group"
                  // controlId="formGridName"
                  >
                    <Form.Label>Dirección:</Form.Label>
                    <Form.Control
                      name="address"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.address}
                      isInvalid={errors.address}
                      disabled={isSubmitting}
                      placeholder="Dirección"
                    />
                    <div className="error-feedback">
                      {errors.address && touched.address && errors.address}
                    </div>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    lg="6"
                    className="mb-3 form-group"
                  // controlId="formGridName"
                  >
                    <Form.Label>Número:</Form.Label>
                    <Form.Control
                      name="streetNumber"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.streetNumber}
                      isInvalid={errors.streetNumber}
                      disabled={isSubmitting}
                      placeholder="Número"
                    />
                    <div className="error-feedback">
                      {errors.streetNumber &&
                        touched.streetNumber &&
                        errors.streetNumber}
                    </div>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    lg="6"
                    className="mb-3 form-group"
                  // controlId="formGridName"
                  >
                    <Form.Label>Colonia:</Form.Label>
                    <Form.Control
                      name="neighborhood"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.neighborhood}
                      isInvalid={errors.neighborhood}
                      disabled={isSubmitting}
                      placeholder="Colonia"
                    />
                    <div className="error-feedback">
                      {errors.neighborhood &&
                        touched.neighborhood &&
                        errors.neighborhood}
                    </div>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    lg="6"
                    className="mb-3 form-group"
                  // controlId="formGridName"
                  >
                    <Form.Label>Referencia:</Form.Label>
                    <Form.Control
                      name="reference"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.reference}
                      isInvalid={errors.reference}
                      disabled={isSubmitting}
                      placeholder="Referencia"
                    />
                    <div className="error-feedback">
                      {errors.reference &&
                        touched.reference &&
                        errors.reference}
                    </div>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    lg="6"
                    className="mb-3 form-group"
                  // controlId="formGridName"
                  >
                    <Form.Label>Código Postal:</Form.Label>
                    <Form.Control
                      name="postalCode"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.postalCode}
                      isInvalid={errors.postalCode}
                      disabled={isSubmitting}
                      placeholder="Código Postal"
                    />
                    <div className="error-feedback">
                      {errors.postalCode &&
                        touched.postalCode &&
                        errors.postalCode}
                    </div>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    lg="6"
                    className="mb-3 form-group"
                  // controlId="formGridName"
                  >
                    <Form.Label>Municipio:</Form.Label>
                    <Form.Control
                      name="municipality"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.municipality}
                      isInvalid={errors.municipality}
                      disabled={isSubmitting}
                      placeholder="Municipio"
                    />
                    <div className="error-feedback">
                      {errors.municipality &&
                        touched.municipality &&
                        errors.municipality}
                    </div>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    lg="6"
                    className="mb-3 form-group"
                  // controlId="formGridName"
                  >
                    <Form.Label>Ciudad:</Form.Label>
                    <Form.Control
                      name="city"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.city}
                      isInvalid={errors.city}
                      disabled={isSubmitting}
                      placeholder="Ciudad"
                    />
                    <div className="error-feedback">
                      {errors.city && touched.city && errors.city}
                    </div>
                  </Form.Group>
                  <Form.Group
                    as={Col}
                    lg="6"
                    className="mb-3 form-group"
                  // controlId="formGridName"
                  >
                    <Form.Label>Estado:</Form.Label>
                    <Form.Control
                      name="state"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.state}
                      isInvalid={errors.state}
                      placeholder="Estado"
                      disabled={isSubmitting}
                    />
                    <div className="error-feedback">
                      {errors.state && touched.state && errors.state}
                    </div>
                  </Form.Group>
                </Row>
                <Row className="client-data__payment">
                  <h5>
                    <span>2</span> Detalles de pago{" "}
                    <img src={lockIconUrl} alt="La Relojeríair" />
                  </h5>
                  <div className="d-flex flex-column flex-lg-row align-items-lg-center client-data__payment__cards mt-2">
                    <p className="mb-2 mb-lg-0 me-2">
                      Aceptamos pagos de las siguientes tarjetas de
                      crédito/débito:
                    </p>
                    <div>
                      <img src={creditCard1Url} alt="La Relojeríair" />
                      <img
                        src={creditCard2Url}
                        className="mx-2"
                        alt="La Relojeríair"
                      />
                      <img src={creditCard3Url} alt="La Relojeríair" />
                    </div>
                  </div>

                  {/* Card Element */}
                  <div className="mt-3">
                    <fieldset style={{ border: "none" }}>
                      <div className="form-row">
                        <div id="card-element">
                          <CardElement
                            options={{
                              style: {
                                base: {
                                  fontSize: "18px",
                                  color: "#424770",
                                  "::placeholder": {
                                    color: "#aab7c4",
                                  },
                                },
                                invalid: {
                                  color: "#9e2146",
                                },
                              },
                              hidePostalCode: false,
                            }}
                          />
                        </div>
                        <div id="card-errors" role="alert" />
                      </div>
                    </fieldset>
                  </div>

                  {/* Installment Plans Selection */}
                  {cardDetailsCollected && availablePlans.length > 0 && (
                    <div className="msi-payment-plans mt-4">
                      <h6 className="msi-payment-plans__title">Plan de pago:</h6>
                      <div className="msi-payment-plans__options">
                        {/* Single payment option */}
                        <div className="msi-payment-plans__option">
                          <input
                            type="radio"
                            id="single-payment"
                            name="payment-plan"
                            value="single"
                            checked={selectedPaymentPlan === "single"}
                            onChange={() => handlePlanSelection("single")}
                            disabled={loading || loadingOrderCreation || criticalError}
                          />
                          <label htmlFor="single-payment" className="msi-payment-plans__label">
                            <span className="msi-payment-plans__label-text">Pago único</span>
                            <span className="msi-payment-plans__label-price">{formatMoney(totalPriceCart)}</span>
                          </label>
                        </div>

                        {/* Installment options */}
                        {availablePlans.map((plan) => (
                          <div key={plan.count} className="msi-payment-plans__option">
                            <input
                              type="radio"
                              id={`msi-${plan.count}`}
                              name="payment-plan"
                              value={plan.count.toString()}
                              checked={selectedPaymentPlan === plan.count.toString()}
                              onChange={() => handlePlanSelection(plan)}
                              disabled={loading || loadingOrderCreation || criticalError}
                            />
                            <label htmlFor={`msi-${plan.count}`} className="msi-payment-plans__label">
                              <span className="msi-payment-plans__label-text">
                                {plan.count} Meses Sin Intereses
                              </span>
                              <div>
                                <span className="msi-payment-plans__label-price">
                                  {formatMoney(totalPriceCart / plan.count)}
                                </span>
                                <span className="msi-payment-plans__label-monthly">/mes</span>
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>

                      {/* <div className="msi-disclaimer mt-2">
                        <small style={{ color: '#666', fontStyle: 'italic' }}>
                          * Las opciones de meses sin intereses están sujetas a la aprobación de tu banco y tarjeta.
                          Solo se muestran las opciones disponibles para tu monto de compra.
                          <br />
                          * Para probar el pago de meses sin intereses, utiliza una tarjeta de crédito válida con saldo disponible.
                          <br />
                          <strong>Tarjeta de prueba específica para meses sin intereses:</strong>
                          <br />
                          - Número: 4000 0048 4000 0008 (soporta 3, 6, 9, 12, 18 y 24 meses)
                          <br />
                          - Fecha de vencimiento: cualquier fecha futura
                          <br />
                          - CVV: 123
                          <br />
                          <strong>Otras tarjetas de prueba:</strong>
                          <br />
                          - Visa: 4242 4242 4242 4242 (sin meses sin intereses)
                          <br />
                          - Mastercard: 5555 5555 5555 4444
                          <br />
                          - American Express: 3782 822463 10005
                        </small>
                      </div> */}
                    </div>
                  )}

                  <div className="msi-disclaimer mt-2">
                    <small style={{ color: '#666', fontStyle: 'italic' }}>
                      * Las opciones de meses sin intereses están sujetas a la aprobación de tu banco y tarjeta.
                      Solo se muestran las opciones disponibles para tu monto de compra.
                      <br />
                      * Para probar el pago de meses sin intereses, utiliza una tarjeta de crédito válida con saldo disponible.
                      <br />
                      <strong>Tarjeta de prueba específica para meses sin intereses:</strong>
                      <br />
                      - Número: 4000 0048 4000 0008 (soporta 3, 6, 9, 12, 18 y 24 meses)
                      <br />
                      - Fecha de vencimiento: cualquier fecha futura
                      <br />
                      - CVV: 123
                      <br />
                      <strong>Otras tarjetas de prueba:</strong>
                      <br />
                      - Visa: 4242 4242 4242 4242 (sin meses sin intereses)
                      <br />
                      - Mastercard: 5555 5555 5555 4444
                      <br />
                      - American Express: 3782 822463 10005
                    </small>
                  </div>

                  {/* No Plans Available Message */}
                  {cardDetailsCollected && availablePlans.length === 0 && (
                    <span
                      style={{
                        color: "#856404",
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffeaa7",
                        borderRadius: "4px",
                        padding: "0.75rem",
                        margin: "0.6rem auto",
                        display: "block",
                        fontSize: "0.9rem",
                        fontWeight: "500",
                        width: "97%",
                      }}
                    >
                      <i className="fas fa-info-circle me-2"></i>
                      No hay planes de meses sin intereses disponibles para esta tarjeta. El pago se realizará en una sola exhibición.
                    </span>
                  )}

                  {/* Error Display */}
                  <span
                    className={formError ? "" : "d-none"}
                    style={{
                      color: formError?.includes("⚠️ IMPORTANTE") ? "#d63384" : "#c93030",
                      fontWeight: "bolder",
                      margin: ".6rem ",
                      padding: formError?.includes("⚠️ IMPORTANTE") ? "1rem" : "0",
                      backgroundColor: formError?.includes("⚠️ IMPORTANTE") ? "#ffeaa7" : "transparent",
                      border: formError?.includes("⚠️ IMPORTANTE") ? "2px solid #d63384" : "none",
                      borderRadius: formError?.includes("⚠️ IMPORTANTE") ? "8px" : "0",
                      display: "block",
                      textAlign: "center"
                    }}
                  >
                    {formError}
                  </span>

                  {/* Action Buttons */}
                  {cardDetailsCollected && (
                    <div className="mb-3">
                      <Button
                        variant="outline-secondary"
                        onClick={resetPaymentForm}
                        disabled={loading || loadingOrderCreation || criticalError}
                        className="me-2 cambiar-tarjeta-btn"
                      >
                        Intentar con otra tarjeta
                      </Button>
                    </div>
                  )}
                </Row>
                <Row className="other-data__payment">
                  <h5>
                    <span>3</span> Otro método de pago
                  </h5>
                  <p>
                    Si quieres un descuento extra en tu compra, el pago debes
                    efectuarlo por medio de retiro sin tarjeta, comunícate con
                    nosotros por{" "}
                    <a
                      href="https://wa.me/525568757619"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className=" fab fa-whatsapp"></i> WhatsApp
                    </a>
                  </p>
                  <span> Sólo aplica en compras menores a $9,000.00</span>
                </Row>
                <Button
                  className={`confirm-payment ${loading || loadingOrderCreation ? "loading" : ""
                    }`}
                  type="submit"
                  disabled={isSubmitting || loading || loadingOrderCreation || criticalError}
                >
                  {loading ? (
                    <span>Verificando tarjeta...</span>
                  ) : loadingOrderCreation ? (
                    <span>Procesando orden...</span>
                  ) : !cardDetailsCollected ? (
                    <span>Pagar y verificar tarjeta para promociones de meses sin intereses</span>
                  ) : (
                    <span>
                      {selectedPaymentPlan === "single"
                        ? `Pagar ${formatMoney(totalPriceCart)}`
                        : `Pagar ${formatMoney(totalPriceCart / parseInt(selectedPaymentPlan))} x ${selectedPaymentPlan} meses`
                      }
                    </span>
                  )}
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      </Col>
      <Col xs="12" lg="4" className="order-summary">
        <OrderSummary
          cartItems={cartItems}
          productsCart={productsCart}
          total={totalPriceCart}
          totalProductsCart={totalProductsCart}
        />
      </Col>
    </Row>
  );
}
